import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, ChevronRight, CheckCircle2, XCircle,
    AlertTriangle, Eye, EyeOff,
} from "lucide-react";
import { publicApi } from "@/lib/publicApi";
import { useAuth } from "@/lib/auth";
import OnboardingHero from "@/components/OnboardingHero";

export default function AcceptInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setSession } = useAuth();
    const [meta, setMeta] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [first, setFirst] = useState("");
    const [last, setLast] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        publicApi.get(`/auth/invite/${token}`)
            .then((r) => setMeta(r.data))
            .catch((err) => setLoadErr({ code: err?.response?.status, detail: err?.response?.data?.detail || "Couldn't load." }));
    }, [token]);

    const strength = useMemo(() => scorePassword(password), [password]);

    const submit = async () => {
        if (!first.trim() || !last.trim()) { toast.error("Both name fields required."); return; }
        if (password !== confirm) { toast.error("Passwords do not match."); return; }
        if (strength.score < 2) { toast.error("Please pick a stronger password."); return; }
        setSubmitting(true);
        try {
            const { data } = await publicApi.post(`/auth/invite/${token}/accept`, {
                first_name: first.trim(),
                last_name: last.trim(),
                password,
            });
            setSession(data.session_token, data.user);
            toast.success("Welcome aboard!");
            navigate(data.redirect || "/admin", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't accept invite.");
        } finally { setSubmitting(false); }
    };

    if (loadErr) return <ErrorScreen code={loadErr.code} detail={loadErr.detail} />;
    if (!meta) return <LoadingScreen />;

    return (
        <div
            className="min-h-screen w-full"
            style={{ background: "linear-gradient(135deg,#061935 0%,#0A2540 55%,#1E6FD9 100%)" }}
            data-testid="accept-invite-page"
        >
            <div className="mx-auto flex min-h-screen max-w-xl flex-col px-5 pb-10">
                <OnboardingHero variant="dark" eyebrow="Finish signup" />
                <main className="mt-2 rounded-2xl bg-white p-7 shadow-xl">
                    <div className="mm-eyebrow" style={{ color: "#1E6FD9" }}>You're invited</div>
                    <h1 className="mm-display mt-1 text-2xl text-mm-navy sm:text-3xl">
                        Hi! {meta.invited_by_name} invited you to join Moving Mountains as <span className="whitespace-nowrap" style={{ color: "#C9A84C" }}>{(/^[aeiou]/i.test(meta.role_label || "") ? "an " : "a ")}{meta.role_label}</span>.
                    </h1>
                    <p className="mt-2 text-sm text-mm-muted">Set up your account in a minute. Your login email is <strong className="text-mm-ink">{meta.email}</strong>.</p>

                    <div className="mt-5 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                                <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">First name</span>
                                <input type="text" value={first} onChange={(e) => setFirst(e.target.value)} className="w-full rounded-md border border-mm-line px-3 py-2 text-sm focus:border-mm-navy focus:outline-none" data-testid="invite-first-name" autoFocus />
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Last name</span>
                                <input type="text" value={last} onChange={(e) => setLast(e.target.value)} className="w-full rounded-md border border-mm-line px-3 py-2 text-sm focus:border-mm-navy focus:outline-none" data-testid="invite-last-name" />
                            </label>
                        </div>
                        <label className="block">
                            <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Password</span>
                            <div className="relative">
                                <input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-md border border-mm-line px-3 py-2 pr-10 text-sm focus:border-mm-navy focus:outline-none" data-testid="invite-password" placeholder="At least 10 characters" />
                                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-mm-muted hover:text-mm-navy" tabIndex={-1}>
                                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                            </div>
                            {password.length > 0 && <StrengthMeter score={strength.score} label={strength.label} />}
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">Confirm password</span>
                            <input type={showPwd ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full rounded-md border border-mm-line px-3 py-2 text-sm focus:border-mm-navy focus:outline-none" data-testid="invite-confirm" />
                            {confirm.length > 0 && password !== confirm && (
                                <div className="mt-1 text-[11px] text-rose-700">Passwords don't match.</div>
                            )}
                        </label>
                    </div>

                    <button
                        onClick={submit}
                        disabled={submitting || !first.trim() || !last.trim() || password.length < 10 || password !== confirm}
                        className="mm-btn-primary mt-6 w-full justify-center disabled:opacity-50"
                        data-testid="invite-submit-btn"
                    >
                        {submitting ? <><Loader2 size={14} className="animate-spin" /> Setting up…</> : <>Accept invitation <ChevronRight size={14} /></>}
                    </button>

                    <p className="mt-4 text-center text-[11px] italic text-mm-muted" data-testid="invite-disclaimer">
                        {meta.compliance_footer || "Moving Mountains will never ask for payment information during onboarding."}
                    </p>
                </main>
            </div>
        </div>
    );
}

function scorePassword(pw) {
    let score = 0;
    if (pw.length >= 10) score++;
    if (pw.length >= 14) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    score = Math.min(score, 4);
    const labels = ["very weak", "weak", "fair", "good", "strong"];
    return { score, label: labels[score] };
}

function StrengthMeter({ score, label }) {
    const colors = ["#9F1239", "#C26A1A", "#C89621", "#0A6E43", "#0A6E43"];
    return (
        <div className="mt-1.5" data-testid="strength-meter">
            <div className="flex h-1 gap-1">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex-1 rounded-full" style={{ background: i < score ? colors[score] : "#E5EAF0" }} />
                ))}
            </div>
            <div className="mt-1 text-[10px]" style={{ color: colors[score] }}>{label}</div>
        </div>
    );
}

function ErrorScreen({ code, detail }) {
    const Icon = code === 410 ? AlertTriangle : XCircle;
    const title = code === 410 ? "This invite link is no longer valid." : code === 404 ? "Invite not found." : "Something went wrong.";
    return (
        <div className="flex min-h-screen items-center justify-center px-5" style={{ background: "linear-gradient(135deg,#061935 0%,#0A2540 55%,#1E6FD9 100%)" }} data-testid="invite-error">
            <div className="w-full max-w-md rounded-2xl bg-white p-7 text-center shadow-xl">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                    <Icon size={32} />
                </div>
                <h2 className="mm-display text-2xl text-mm-navy">{title}</h2>
                <p className="mt-2 text-sm text-mm-muted">{detail}</p>
                <p className="mt-5 text-xs text-mm-muted">Reach out to whoever invited you and ask for a fresh link.</p>
            </div>
        </div>
    );
}

function LoadingScreen() {
    return <div className="flex min-h-screen items-center justify-center" style={{ background: "#0A2540" }}><Loader2 className="animate-spin text-white" size={36} /></div>;
}
